import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { TailSpin } from 'react-loader-spinner';
import Colors from '../../library/Colors';
import { tcPost } from './TimeClockDialogs';
import { errorText, surface, tone } from './tcFormat';

/** The worker's defaults, shown when a database has no row for a variable. */
const DEFAULTS = {
    TimeClockMinIntervalSec: '60',
    TimeClockAutoCloseAt: '04:00',
    TimeClockMaxShiftMin: '720',
    TimeClockBreakMode: 'EXPLICIT',
    TimeClockBreakDeductAfterMin: '360',
    TimeClockBreakDeductMin: '30',
    TimeClockAllowedSources: 'POS,OFFICE,ANDROID,IOS,WEB',
    TimeClockAllowPersonal: 'T',
    TimeClockPhoneGeofenceM: '0',
    TimeClockLateSyncSec: '300',
    TimeClockOfflineMaxHours: '48',
    TimeClockSelfCorrection: 'T',
    TimeClockPresenceOnKiosk: 'F',
    TimeClockLegacyWriteThrough: 'T',
};

const SOURCES = ['POS', 'OFFICE', 'ANDROID', 'IOS', 'WEB'];

/**
 * TIMECLOCK program variables (kasvar): globally or per store. A store value
 * overrides the global one; register values (set in MySales) are listed but
 * not edited here.
 */
const TimeClockSettingsTab = ({ t, theme, api, notify, reloadKey, onLoaded }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [scope, setScope] = useState('0000');

    useEffect(() => {
        tcPost('settings', api).then((response) => {
            if (response?.success) { setData(response.data); if (onLoaded) onLoaded(); }
            else setError(errorText(t, response));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, reloadKey]);

    const s = surface(theme);
    const panel = { ...s.card, overflow: 'hidden' };
    const rowLine = s.line;

    if (error) return <Typography sx={{ color: tone.bad, fontSize: 14 }}>{error}</Typography>;
    if (!data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <TailSpin height="36" width="36" color={Colors.sft_blue} ariaLabel="loading" />
            </Box>
        );
    }

    const valueAt = (name, code) => {
        const row = (data.values || []).find((v) => v.var === name && v.code === code);
        return row ? row.value : null;
    };
    const globalValue = (name) => valueAt(name, '0000') ?? DEFAULTS[name];

    const minRadius = data.geofence_min_m || 150;

    const save = (name, value) => {
        if (name === 'TimeClockPhoneGeofenceM' && Number(value) > 0 && Number(value) < minRadius) {
            if (notify) notify(t('tc_err_geofence_min', 'Use 0 (off) or at least 150 m.'), false);
            return Promise.resolve(false);
        }
        return saveValue(name, value);
    };

    const saveValue = (name, value) => tcPost('saveSetting', api, { var: name, code: scope, value }).then((response) => {
        if (response?.success) { setData(response.data); if (notify) notify(t('tc_saved', 'Saved'), true); return true; }
        if (notify) notify(errorText(t, response), false);
        return false;
    });

    const clear = (name) => tcPost('clearSetting', api, { var: name, code: scope }).then((response) => {
        if (response?.success) { setData(response.data); if (notify) notify(t('tc_follows_global_done', 'The store follows the global value again'), true); }
        else if (notify) notify(errorText(t, response), false);
    });

    const breakMode = (scope !== '0000' ? valueAt('TimeClockBreakMode', scope) : null) ?? globalValue('TimeClockBreakMode');

    const groups = [
        {
            title: t('tc_set_group_stamping', 'Stamping'),
            items: [
                { name: 'TimeClockMinIntervalSec', type: 'int', unit: t('tc_unit_seconds', 'seconds'), label: t('tc_set_min_interval', 'Minimum time between two stamps'), hint: t('tc_set_min_interval_hint', 'A second stamp by the same person within this time is refused ("already stamped at …").') },
                { name: 'TimeClockAllowedSources', type: 'list', label: t('tc_set_sources', 'Where may people stamp'), hint: t('tc_set_sources_hint', 'Till, MyFashionManager, the Android and iOS app, MySoftTouch (corrections).') },
                { name: 'TimeClockAllowPersonal', type: 'bool', label: t('tc_set_personal', 'Stamp on own phone'), hint: t('tc_set_personal_hint', 'Employees may stamp in the MyFasMan Mobile app on their own phone. Off: only at the kiosk or the till.') },
                { name: 'TimeClockPhoneGeofenceM', type: 'int', unit: 'm', label: t('tc_set_geofence', 'Own phone only near the store'), hint: t('tc_set_geofence_hint2', '0 = off. Otherwise at least 150 m, because phone GPS inside a building is not precise. The app then asks for the location once when stamping on the own phone; only the result (near the store or not) is kept, never the position.') },
                { name: 'TimeClockPresenceOnKiosk', type: 'bool', label: t('tc_set_presence_kiosk', 'Show who is in on the kiosk'), hint: t('tc_set_presence_kiosk_hint', 'Names of the people clocked in, visible to everyone at the kiosk.') },
            ],
        },
        {
            title: t('tc_set_group_days', 'Days and breaks'),
            items: [
                { name: 'TimeClockBreakMode', type: 'enum', label: t('tc_set_break_mode', 'Breaks'), hint: t('tc_set_break_mode_hint', 'Stamped: people clock out and in for a break (advised). Deducting a break nobody took is not what actually happened.'),
                    options: [['EXPLICIT', t('tc_break_explicit', 'Stamped by the employee')], ['NONE', t('tc_break_none', 'No breaks')], ['DEDUCT', t('tc_break_deduct', 'Deduct automatically (not advised)')]] },
                { name: 'TimeClockBreakDeductAfterMin', type: 'int', unit: t('tc_unit_minutes', 'minutes'), label: t('tc_set_deduct_after', 'Deduct a break after'), hint: t('tc_set_deduct_after_hint', 'Only with automatic deduction. Days where it applied are marked in every export.'), hidden: breakMode !== 'DEDUCT' },
                { name: 'TimeClockBreakDeductMin', type: 'int', unit: t('tc_unit_minutes', 'minutes'), label: t('tc_set_deduct_min', 'Minimum break'), hint: '', hidden: breakMode !== 'DEDUCT' },
                { name: 'TimeClockMaxShiftMin', type: 'int', unit: t('tc_unit_minutes', 'minutes'), label: t('tc_set_max_shift', 'Longest normal shift'), hint: t('tc_set_max_shift_hint', 'Longer shifts are flagged for review (720 = 12 hours).') },
                { name: 'TimeClockAutoCloseAt', type: 'time', label: t('tc_set_auto_close', 'Close a forgotten shift at'), hint: t('tc_set_auto_close_hint', 'Next day at this hour a shift without OUT becomes incomplete, so it shows up under "To handle".') },
                { name: 'TimeClockSelfCorrection', type: 'bool', label: t('tc_set_self_correction', 'Employees may request corrections'), hint: t('tc_set_self_correction_hint', 'In the app, for their own days. A manager approves.') },
            ],
        },
        {
            title: t('tc_set_group_offline', 'Offline and technical'),
            items: [
                { name: 'TimeClockOfflineMaxHours', type: 'int', unit: t('tc_unit_hours', 'hours'), label: t('tc_set_offline_max', 'Accept offline stamps up to'), hint: t('tc_set_offline_max_hint', 'Older offline stamps wait for a manager.') },
                { name: 'TimeClockLateSyncSec', type: 'int', unit: t('tc_unit_seconds', 'seconds'), label: t('tc_set_late_sync', 'Mark as late after'), hint: t('tc_set_late_sync_hint', 'A stamp that reaches the server later than this is marked "sent late".') },
                { name: 'TimeClockLegacyWriteThrough', type: 'bool', label: t('tc_set_legacy', 'Also write to the old time clock table'), hint: t('tc_set_legacy_hint', 'Keeps older till and office versions and report 1293 working during the transition.') },
            ],
        },
    ];

    const registerCodes = [...new Set((data.registers_overridden || []).map((r) => r.code))];
    const geofenceOn = (data.values || []).some((v) => v.var === 'TimeClockPhoneGeofenceM' && Number(v.value) > 0);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 980 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField select size="small" label={t('tc_applies_to', 'Applies to')} value={scope} onChange={(e) => setScope(e.target.value)} sx={{ minWidth: 240 }}>
                    <MenuItem value="0000">{t('tc_all_stores_global', 'All stores (global)')}</MenuItem>
                    {(data.stores || []).map((store) => (
                        <MenuItem key={store.code} value={'00' + store.code}>{t('tc_store', 'Store')} {store.code} — {store.oms}</MenuItem>
                    ))}
                </TextField>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', flex: 1, minWidth: 240 }}>
                    {scope === '0000'
                        ? t('tc_scope_global_hint', 'Valid for every store, unless a store has its own value.')
                        : t('tc_scope_store_hint', 'Switch on "Own value" to deviate from the global setting for this store.')}
                </Typography>
            </Box>

            {groups.map((group) => (
                <Box key={group.title} sx={panel}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, px: 2, minHeight: 48, display: 'flex', alignItems: 'center' }}>{group.title}</Typography>
                    {group.items.filter((item) => !item.hidden).map((item) => {
                        const own = scope === '0000' ? valueAt(item.name, '0000') : valueAt(item.name, scope);
                        const inherited = scope !== '0000' && own === null;
                        const value = own ?? globalValue(item.name);
                        return (
                            <SettingRow key={item.name + scope} item={item} value={value} inherited={inherited} scope={scope}
                                t={t} rowLine={rowLine} onSave={(v) => save(item.name, v)} onClear={() => clear(item.name)} />
                        );
                    })}
                </Box>
            ))}

            {geofenceOn && (
                <StoreLocations t={t} api={api} notify={notify} panel={panel} rowLine={rowLine}
                    stores={data.store_locations || []} onSaved={setData} />
            )}

            {registerCodes.length > 0 && (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    {t('tc_register_overrides', 'Registers with their own time clock values (set in MySales)')}: {registerCodes.join(', ')}
                </Typography>
            )}
        </Box>
    );
};

/** OpenStreetMap link to check a position. */
const mapLink = (position) => {
    const match = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,;]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(position || '');
    if (!match) return null;
    return 'https://www.openstreetmap.org/?mlat=' + match[1] + '&mlon=' + match[2] + '#map=18/' + match[1] + '/' + match[2];
};

/**
 * Where each store is, for "own phone only near the store". Without a
 * position the server cannot check and lets the stamp through, marked.
 */
const StoreLocations = ({ t, api, notify, panel, rowLine, stores, onSaved }) => {
    const missing = stores.filter((store) => !store.location).length;
    return (
        <Box sx={panel}>
            <Box sx={{ px: 2, py: 1.5 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{t('tc_store_locations', 'Store locations')}</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', maxWidth: '75ch' }}>
                    {t('tc_store_locations_hint', 'Needed for "Own phone only near the store". Look the position up from the address or use your current location when you are in the store, then check it on the map. A store without a position is not checked: the stamp is accepted and marked.')}
                </Typography>
                {missing > 0 && (
                    <Typography sx={{ fontSize: 12.5, color: tone.warn, fontWeight: 600, mt: 0.5 }}>
                        {t('tc_store_locations_missing', 'Stores without a position')}: {missing}
                    </Typography>
                )}
            </Box>
            {stores.map((store) => (
                <StoreLocationRow key={store.code} store={store} t={t} api={api} notify={notify} rowLine={rowLine} onSaved={onSaved} />
            ))}
        </Box>
    );
};

const StoreLocationRow = ({ store, t, api, notify, rowLine, onSaved }) => {
    const [draft, setDraft] = useState(store.location || '');
    const [found, setFound] = useState('');
    const [busy, setBusy] = useState(false);
    useEffect(() => { setDraft(store.location || ''); }, [store.location]);

    const dirty = draft.trim() !== (store.location || '');
    const link = mapLink(draft);
    const small = { textTransform: 'none', minWidth: 0, px: 1.25, height: 40, whiteSpace: 'nowrap' };

    const lookUp = () => {
        setBusy(true);
        setFound('');
        tcPost('geocodeStore', api, { store_id: store.code }).then((response) => {
            setBusy(false);
            if (response?.success) {
                setDraft(response.data.location);
                setFound(response.data.found || '');
            } else if (notify) {
                notify(errorText(t, response), false);
            }
        });
    };

    const here = () => {
        if (!navigator.geolocation) { if (notify) notify(t('tc_err_no_browser_location', 'This browser cannot give a location.'), false); return; }
        setBusy(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setBusy(false);
                setDraft(position.coords.latitude.toFixed(6) + ',' + position.coords.longitude.toFixed(6));
                setFound(t('tc_my_location_accuracy', 'Your location, accurate to about') + ' ' + Math.round(position.coords.accuracy) + ' m');
            },
            () => { setBusy(false); if (notify) notify(t('tc_err_no_browser_location', 'This browser cannot give a location.'), false); },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
        );
    };

    const save = (value) => {
        setBusy(true);
        tcPost('saveStoreLocation', api, { store_id: store.code, location: value }).then((response) => {
            setBusy(false);
            if (response?.success) {
                setFound('');
                onSaved(response.data);
                if (notify) notify(t('tc_saved', 'Saved'), true);
            } else if (notify) {
                notify(errorText(t, response), false);
            }
        });
    };

    return (
        <Box sx={{
            display: 'grid', alignItems: 'center', columnGap: 2, rowGap: 1, px: 2, py: 1.25, borderTop: rowLine,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 490px' },
        }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{store.code} — {store.oms}</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{store.address || t('tc_no_address', 'No address')}</Typography>
                {found && <Typography sx={{ fontSize: 12, color: tone.info }}>{found}</Typography>}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap' }}>
                <Button size="small" sx={small} disabled={busy || !store.address} onClick={lookUp}>{t('tc_look_up', 'Look up')}</Button>
                <Button size="small" sx={small} disabled={busy} onClick={here}>{t('tc_my_location', 'My location')}</Button>
                <TextField size="small" value={draft} placeholder="51.054340,3.717420" disabled={busy}
                    onChange={(e) => { setDraft(e.target.value); setFound(''); }}
                    sx={{ width: 200 }} slotProps={{ htmlInput: { spellCheck: false } }} />
                {dirty && draft.trim() !== '' ? (
                    <Button size="small" variant="contained" disableElevation sx={small} disabled={busy} onClick={() => save(draft.trim())}>{t('save', 'Save')}</Button>
                ) : link ? (
                    <Button size="small" sx={small} component="a" href={link} target="_blank" rel="noopener noreferrer">{t('tc_map', 'Map')}</Button>
                ) : null}
                {!dirty && store.location && (
                    <Button size="small" color="error" sx={small} disabled={busy} onClick={() => save('')}>{t('tc_remove', 'Remove')}</Button>
                )}
            </Box>
        </Box>
    );
};

const SettingRow = ({ item, value, inherited, scope, t, rowLine, onSave, onClear }) => {
    const [draft, setDraft] = useState(value);
    const [busy, setBusy] = useState(false);
    useEffect(() => { setDraft(value); }, [value]);

    const dirty = String(draft) !== String(value);
    const commit = (next) => {
        setBusy(true);
        onSave(next).then(() => setBusy(false));
    };

    let control = null;
    const disabled = inherited || busy;
    switch (item.type) {
        case 'bool':
            // The switch has 12px inner padding; pull it right so its track
            // ends on the same line as the input fields.
            control = <Switch checked={value === 'T'} disabled={disabled} onChange={(e) => commit(e.target.checked ? 'T' : 'F')} sx={{ mr: '-12px' }} />;
            break;
        case 'enum':
            control = (
                <TextField select size="small" value={value} disabled={disabled} onChange={(e) => commit(e.target.value)} sx={{ width: 280 }}>
                    {item.options.map(([code, label]) => <MenuItem key={code} value={code}>{label}</MenuItem>)}
                </TextField>
            );
            break;
        case 'list': {
            const active = String(value).split(',').map((s) => s.trim()).filter(Boolean);
            const labels = { POS: t('tc_source_pos', 'Till'), OFFICE: 'MyFashionManager', ANDROID: 'Android', IOS: 'iOS', WEB: 'MySoftTouch' };
            control = (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {SOURCES.map((source) => {
                        const on = active.includes(source);
                        return (
                            <Chip key={source} size="small" label={labels[source]} disabled={disabled}
                                color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                                onClick={() => {
                                    const next = on ? active.filter((s) => s !== source) : [...active, source];
                                    if (next.length === 0) return;
                                    commit(SOURCES.filter((s) => next.includes(s)).join(','));
                                }} />
                        );
                    })}
                </Box>
            );
            break;
        }
        default:
            // Save sits left of the field, so the field keeps its place.
            control = (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {dirty && <Button size="small" variant="contained" disableElevation disabled={busy} onClick={() => commit(draft)} sx={{ textTransform: 'none' }}>{t('save', 'Save')}</Button>}
                    <TextField size="small" value={draft} disabled={disabled} type={item.type === 'time' ? 'time' : 'text'}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && dirty) commit(draft); }}
                        sx={{ width: 130 }}
                        slotProps={{ htmlInput: { inputMode: item.type === 'int' ? 'numeric' : undefined, style: { textAlign: 'right' } } }} />
                </Box>
            );
    }

    // Fixed columns: label | (own value) | control, right-aligned | unit.
    // Every control ends on the same vertical line; the unit strip is
    // always there, empty when a setting has no unit.
    const storeScope = scope !== '0000';
    return (
        <Box sx={{
            display: 'grid', alignItems: 'center', columnGap: 2, rowGap: 1, px: 2, py: 1.25, borderTop: rowLine,
            gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'minmax(0, 1fr) ' + (storeScope ? '130px ' : '') + '490px',
            },
        }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{item.label}</Typography>
                {item.hint && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', maxWidth: '62ch' }}>{item.hint}</Typography>}
            </Box>
            {storeScope && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t('tc_own_value', 'Own value')}</Typography>
                    <Switch size="small" checked={!inherited} disabled={busy}
                        onChange={(e) => (e.target.checked ? commit(value) : onClear())} />
                </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, opacity: inherited ? 0.55 : 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', flex: { md: 1 }, minWidth: 0 }}>{control}</Box>
                <Typography sx={{ width: 72, flexShrink: 0, pl: 1.25, fontSize: 13, color: 'text.secondary' }}>{item.unit || ''}</Typography>
            </Box>
        </Box>
    );
};

export default TimeClockSettingsTab;
